// File: app/src/components/workout/FreeTrainingLog.js
//
// Free training's log (#157): what you've done this session, under the Coach's
// dial, with the box to add to it at the bottom, in thumb reach.
//
// Typing is the main input: one box filters your own exercise list (built from
// your history) and always offers "Add …" for something new. Numbers typed
// with the name ("bench 80x8", "row 2k") log at once; without them the number
// sheet opens, filled from last time. Three quick buttons hold this session's
// exercises and your most used, and the newest entry has Same again.
//
// Each entry is the plan step it'll be saved as (utils/freeTrainingLog), and
// has the plan step's ⋯ menu (Edit, Duplicate, Delete). Edit, or tapping its
// name, opens the plan editor's own step dialog; a set's number edits that
// set. There's no drag here: the log's order is the session's timeline.
// Reordering and grouping happen at Finish, in the plan editor.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Copy,
  ListChecks,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { AddRow, ChipGrid, Group } from "../profile/ProfileParts";
import {
  HoldStepper,
  PieceFields,
  RepsStepper,
  RoundsStepper,
  WeightStepper,
  kindStyle,
} from "./freeTrainingParts";
import ChecklistSheet, { ChecklistRow } from "./FreeTrainingChecklist";
import ExerciseDetailDialog from "../plan/ExerciseDetailDialog";
import StepAmountFields from "../plan/StepAmountFields";
import { isRecoveryType } from "../../utils/planNodeUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useToast } from "../../contexts/ToastContext";
import { useConnectedEquipment } from "../../contexts/ConnectedEquipmentContext";
import { IDLE_MS, createPieceDetector, machineName } from "../../utils/machinePieces";
import { cn } from "../../lib/utils";
import { shownWeight } from "../../utils/units";
import {
  BLOCKS,
  DEFAULT_BLOCK_MOVES,
  addBlock,
  addPiece,
  addSets,
  defaultLast,
  deleteEntry,
  deleteSet,
  describeEntry,
  describeLast,
  entryAt,
  checklistEmpty,
  checklistFigure,
  checklistItems,
  checklistNext,
  checklistProgress,
  editEntryNode,
  entryView,
  sameExercise,
  exerciseId,
  formatClock,
  formatPiece,
  formatSet,
  isHold,
  pieceFromParsed,
  typedName,
  formatLoad,
  typedLoadKg,
  hasNumbers,
  identifyExercise,
  loggedCount,
  parseEntryText,
  splitEntries,
  quickPicks,
  splitBlockText,
  repeatEntry,
  suggestExercises,
  updateSet,
  withSession,
} from "../../utils/freeTrainingLog";

const NEW_KINDS = [
  { value: "lift", label: "Weights" },
  { value: "body", label: "Bodyweight" },
  { value: "cardio", label: "Distance or time" },
  { value: "hold", label: "Timed hold" },
];


const blockInfo = (name) => BLOCKS.find((b) => b.name === name) || BLOCKS[0];

// Raised, so it sits above the box you type in rather than over it.
const toast = (showToast, title) => showToast({ title, duration: 2000, raised: true });

/**
 * A paired rower or bike logs its own pieces (#158): read it four times a
 * second (once a second put a piece's time up to a second out) and, when it
 * goes still after real work, log the piece with its own start.
 */
const useMachinePieces = (onChange, showToast) => {
  const { active } = useConnectedEquipment();
  const activeRef = useRef(active);
  activeRef.current = active;
  const connected = active?.status === "connected";
  const type = active?.type;
  useEffect(() => {
    if (!connected || !type) return undefined;
    const detector = createPieceDetector();
    const id = setInterval(() => {
      const reading = activeRef.current?.getFreshSnapshot?.(3000) || null;
      const piece = detector.push(reading, Date.now());
      if (!piece) return;
      const name = machineName(type);
      const metric = piece.m ? "distance" : "time";
      const entry = { name, metric, m: piece.m, s: piece.s, source: type };
      // It ended when the machine went still, a few seconds before now.
      onChange((l, now) => {
        const at = Math.max(0, now - Math.round(IDLE_MS / 1000));
        return addPiece(l, { ...entry, from: Math.max(0, at - piece.s) }, at);
      });
      const time = metric === "distance" ? ` · ${formatClock(piece.s)}` : "";
      toast(showToast, `${name} · ${formatPiece(entry)}${time}, from the ${name.toLowerCase()}`);
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, type]);
  return connected ? type : null;
};

const LogButton = ({ children, ...props }) => (
  <Button type="button" className="h-12 w-full gap-2 text-base font-semibold" {...props}>
    <Plus className="h-5 w-5" />
    {children}
  </Button>
);

// ─── Sheets ─────────────────────────────────────────────────────────────────

// A name kept as typed, its first letter raised.
const asTyped = (raw) => {
  const text = String(raw || "").replace(/\s+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

// What a block is of: your usual moves, or type any other.
const MovePicker = ({ moves, value, onChange, hint }) => {
  const [other, setOther] = useState(value && !moves.includes(value) ? value : "");
  return (
    <div>
      <p className="mb-2 flex items-baseline justify-between text-sm font-medium">
        Doing {hint && <span className="text-xs font-normal text-muted-foreground">{hint}</span>}
      </p>
      <ChipGrid
        columns={3}
        options={moves.map((m) => ({ value: m, label: m }))}
        value={other.trim() ? null : value}
        onChange={(m) => {
          setOther("");
          onChange(m);
        }}
      />
      <input
        type="text"
        value={other}
        onChange={(e) => {
          setOther(e.target.value);
          const typed = e.target.value.trim();
          // As typed: a move of your own isn't renamed.
          if (typed) onChange(asTyped(typed));
          else if (!moves.includes(value)) onChange(moves[0]);
        }}
        placeholder="Something else"
        aria-label="Something else"
        autoComplete="off"
        className="mt-2 h-11 w-full rounded-xl border border-border bg-card px-3.5 text-base outline-none placeholder:text-muted-foreground focus:border-primary/45 focus:ring-[3px] focus:ring-primary/15"
      />
    </div>
  );
};

// Numbers for something you're about to log, filled from what you typed, from
// the checklist (`description` says so), or from last time.
// Is this exercise the machine that's paired? Then its pieces log themselves.
const isPairedMachine = (name, type) =>
  type === "rower" ? /\brow/i.test(name || "") : type === "indoor_bike" ? /bike|cycl/i.test(name || "") : false;

const NumbersSheet = ({ ex, parsed = {}, moves, description, pairedMachine = null, onClose, onLog }) => {
  const last = ex.last || defaultLast(ex.kind, ex.name);
  const [kg, setKg] = useState(typedLoadKg(parsed) ?? last.kg ?? 0);
  const [reps, setReps] = useState(parsed.reps ?? last.reps ?? 10);
  // Reps, or for time (a hold, or anything you choose to do for time).
  const [setUnit, setSetUnit] = useState(isHold(ex) || (parsed.s && parsed.reps == null) ? "s" : "reps");
  const hold = setUnit === "s";
  const [held, setHeld] = useState(parsed.s ?? last.s ?? 30);
  // A piece: what was typed ("bike 20 cal"), else last time's way of doing it.
  const [piece, setPiece] = useState(() => {
    const typed = pieceFromParsed(parsed);
    return {
      metric: typed?.metric || last.metric || "distance",
      m: parsed.m || last.m || null,
      s: (typed?.metric === "time" ? parsed.s : null) || last.s || null,
      cal: parsed.cal || last.cal || null,
    };
  });
  const [rounds, setRounds] = useState(parsed.rounds || last.rounds || blockInfo(ex.name).rounds);
  const [of, setOf] = useState(parsed.of || last.of || moves[0]);

  let body;
  let footer;
  if (ex.kind === "cardio") {
    body = (
      <>
        {isPairedMachine(ex.name, pairedMachine) && (
          <p className="text-xs leading-snug text-muted-foreground">
            {`Your ${machineName(pairedMachine).toLowerCase()} is paired, so each piece logs itself when you stop. Log here only what it missed.`}
          </p>
        )}
        <PieceFields piece={piece} onChange={(patch) => setPiece((p) => ({ ...p, ...patch }))} presets />
      </>
    );
    // A time typed with a distance or calories ("row 2k 7:45") goes with it.
    const { metric } = piece;
    footer = (
      <LogButton
        onClick={() =>
          onLog(
            metric === "time"
              ? { metric, s: piece.s || 600 }
              : metric === "cal"
                ? { metric, cal: piece.cal || 20, s: parsed.s || null }
                : { metric, m: piece.m || 1000, s: parsed.s || null },
          )
        }
      >
        Log it
      </LogButton>
    );
  } else if (ex.kind === "block") {
    const info = blockInfo(ex.name);
    body = (
      <>
        <MovePicker moves={moves} value={of} onChange={setOf} hint={info.hint} />
        <RoundsStepper name={ex.name} value={rounds} onChange={setRounds} />
      </>
    );
    footer = <LogButton onClick={() => onLog({ rounds, of })}>Log it</LogButton>;
  } else {
    const sameAsLast =
      ex.n > 0 && (hold ? last.s != null : last.reps != null) && !hasNumbers(parsed) && !description;
    body = (
      <>
        <StepAmountFields
          units={["reps", "s"]}
          unit={setUnit}
          onUnit={setSetUnit}
          value={hold ? held : reps}
          onValue={hold ? setHeld : setReps}
          load={kg}
          onLoad={setKg}
          name={ex.name}
          loadOffLabel="BW"
        />
        {sameAsLast && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/45 bg-primary/10 px-3.5 py-2.5">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary">Last time</p>
              <p className="font-timer text-base font-bold tabular-nums">{describeLast(ex.kind, last)}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setKg(last.kg ?? 0);
                if (hold) setHeld(last.s);
                else setReps(last.reps);
              }}
              className="text-sm font-semibold text-primary"
            >
              Same again
            </button>
          </div>
        )}
      </>
    );
    footer = <LogButton onClick={() => onLog(hold ? { kg, s: held } : { kg, reps })}>Log set</LogButton>;
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={ex.name}
      description={description}
      showDescription={!!description}
      footer={footer}
    >
      {body}
    </DetailSheet>
  );
};

// One set's weight and reps, or delete it.
const SetSheet = ({ entry, index, onClose, onChange }) => {
  const set = entry.sets[index];
  const [kg, setKg] = useState(set.kg);
  const [reps, setReps] = useState(set.reps);
  // A hold's set is its time.
  const [held, setHeld] = useState(set.s);
  return (
    <DetailSheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={`${entry.name} · set ${index + 1}`}
      footer={
        <div className="grid gap-2">
          <Button
            type="button"
            className="h-12 w-full text-base font-semibold"
            onClick={() => {
              onChange((log) => updateSet(log, entry.id, index, set.s != null ? { kg, s: held } : { kg, reps }));
              onClose();
            }}
          >
            Done
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full text-destructive hover:text-destructive"
            onClick={() => {
              onChange((log) => deleteSet(log, entry.id, index));
              onClose();
            }}
          >
            Delete this set
          </Button>
        </div>
      }
    >
      <WeightStepper name={entry.name} value={kg} onChange={setKg} />
      {set.s != null ? <HoldStepper value={held} onChange={setHeld} /> : <RepsStepper value={reps} onChange={setReps} />}
    </DetailSheet>
  );
};

// Edit an entry in the plan editor's own step dialog, the one every session
// uses: its name, type, time / reps / calories, load, effort and notes. A
// sets block opens on its step with its set count, so each set's numbers can
// be changed too. The entry is the step it'll be saved as, so what's set here
// is what's saved.
const EntryStepDialog = ({ node, onClose, onChange, onDuplicate }) => {
  const isSets = Array.isArray(node.children) && node.children.length > 0 && node.unit === "sets";
  const step = isSets ? node.children.find((c) => !isRecoveryType(c.type)) || node.children[0] : node;
  return (
    <ExerciseDetailDialog
      open
      onOpenChange={(open) => !open && onClose()}
      node={step}
      indexPath={[]}
      hasChildren={!isSets && Array.isArray(node.children) && node.children.length > 0}
      setCount={isSets ? Number(node.value) || 1 : 1}
      onSave={(_, updates) => onChange((log) => editEntryNode(log, node.id, updates))}
      onDelete={() => onChange((log) => deleteEntry(log, node.id))}
      onDuplicate={onDuplicate}
    />
  );
};

// Something not on your list yet: say what kind it is, then the numbers.
const NewExerciseSheet = ({ name, parsed, onClose, onNext }) => {
  const [kind, setKind] = useState(parsed.m || parsed.s || parsed.cal ? "cardio" : "lift");
  return (
    <DetailSheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={name}
      footer={
        <Button type="button" className="h-12 w-full text-base font-semibold" onClick={() => onNext(kind)}>
          Next
        </Button>
      }
    >
      <div>
        <p className="mb-2 flex items-baseline justify-between text-sm font-medium">
          What kind is it? <span className="text-xs font-normal text-muted-foreground">it&apos;s added to your list</span>
        </p>
        <ChipGrid columns={2} options={NEW_KINDS} value={kind} onChange={setKind} />
      </div>
    </DetailSheet>
  );
};

// ─── The log ────────────────────────────────────────────────────────────────

const EntryMenu = ({ entry, onEdit, onDuplicate, onDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label={`Actions for ${entry.name}`}
        title="Step actions"
        className="rounded p-1 text-muted-foreground/60 transition-colors hover:text-foreground"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-44">
      <DropdownMenuItem onSelect={onEdit}>
        <Pencil className="mr-2 h-3.5 w-3.5" />
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={onDuplicate}>
        <Copy className="mr-2 h-3.5 w-3.5" />
        Duplicate
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
        <Trash2 className="mr-2 h-3.5 w-3.5" />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const entryValue = (entry) => {
  if (entry.sets) {
    const kgs = [...new Set(entry.sets.map((s) => s.kg))];
    return (
      <>
        {kgs.length === 1 ? formatLoad(kgs[0]) : "mixed"}
        <small className="font-medium text-muted-foreground">{` · ${entry.sets.length} ${entry.sets.length === 1 ? "set" : "sets"}`}</small>
      </>
    );
  }
  if (entry.kind === "cardio") {
    return (
      <>
        {formatPiece(entry)}
        {entry.metric !== "time" && entry.s ? (
          <small className="font-medium text-muted-foreground">{` · ${formatClock(entry.s)}`}</small>
        ) : null}
      </>
    );
  }
  return (
    <>
      {`${entry.rounds}×`}
      <small className="font-medium text-muted-foreground">{` ${entry.of}`}</small>
    </>
  );
};

const LogEntry = ({ entry, current, onEdit, onEditSet, onAgain, onDuplicate, onDelete }) => {
  const style = kindStyle(entry.kind);
  const Icon = style.icon;
  const sameLoad = entry.sets && new Set(entry.sets.map((s) => s.kg)).size === 1;
  return (
    <article
      className={cn(
        "relative grid gap-2 overflow-hidden rounded-[10px] border py-2.5 pl-4 pr-2",
        current ? "border-primary/45 bg-primary/10" : "border-border bg-card",
      )}
    >
      <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1", style.rail)} />
      <div className="grid grid-cols-[18px_minmax(0,1fr)_auto_auto] items-center gap-2">
        <Icon className={cn("h-4 w-4", style.text)} aria-hidden="true" />
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${entry.name}`}
          className="flex min-w-0 items-center text-left font-semibold"
        >
          <span className="truncate">{entry.name}</span>
          {entry.source && (
            <span className="ml-1.5 shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {`From ${machineName(entry.source).toLowerCase()}`}
            </span>
          )}
          <Pencil className="ml-1.5 h-3 w-3 shrink-0 text-muted-foreground/70" aria-hidden="true" />
        </button>
        <span className="whitespace-nowrap font-timer text-sm font-semibold tabular-nums">{entryValue(entry)}</span>
        <EntryMenu entry={entry} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
      </div>
      {entry.sets && (
        <div className="flex flex-wrap items-center gap-1.5 pl-[26px]">
          {entry.sets.map((s, i) => (
            <button
              key={`${i}-${s.at}`}
              type="button"
              onClick={() => onEditSet(i)}
              aria-label={`Set ${i + 1}, ${s.s != null ? formatClock(s.s) : `${s.reps} reps`} at ${formatLoad(s.kg)}. Change it.`}
              className="min-w-[34px] rounded-lg border border-border bg-muted/40 px-2 py-1 text-center font-timer text-[13px] font-semibold tabular-nums"
            >
              {s.s != null ? formatClock(s.s) : s.reps}
              {!sameLoad && <small className="font-medium text-muted-foreground">{`@${s.kg ? shownWeight(s.kg) : "BW"}`}</small>}
            </button>
          ))}
        </div>
      )}
      {current && (
        <div className="flex items-center justify-between gap-2.5 pl-[26px]">
          <span className="text-xs text-muted-foreground">
            {"Last at "}
            <b className="font-timer font-semibold tabular-nums text-foreground/70">{formatClock(entryAt(entry))}</b>
          </span>
          <Button type="button" size="sm" onClick={onAgain} className="h-9 gap-1.5 px-3 font-semibold">
            <Plus className="h-4 w-4" />
            {entry.sets ? "Same again" : "Again"}
          </Button>
        </div>
      )}
    </article>
  );
};

/**
 * @param {object} props
 * @param {any[]} props.log         coachState.freeLog: plan steps (see utils/freeTrainingLog)
 * @param {any[]} props.list        your exercise list (buildExerciseList)
 * @param {(change: (log: any[], now: number) => any[]) => void} props.onChange
 * @param {any} [props.checklist]  coachState.freeChecklist (#164): a plan, or null
 * @param {(change: (checklist: any[]) => any[]) => void} [props.onChecklistChange]
 * @param {any[]} [props.checklistSessions]  sessions to start one from (checklistSessions)
 */
export default function FreeTrainingLog({
  log = [],
  list = [],
  onChange,
  checklist = null,
  onChecklistChange,
  checklistSessions = [],
}) {
  const { showToast, clearToasts } = useToast();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [sheet, setSheet] = useState(null);
  const composerRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const loggedSoFar = loggedCount(log);
  const pairedMachine = useMachinePieces(onChange, showToast);

  // A sheet or dialog opening clears the "logged" toasts, which would sit
  // over it.
  useEffect(() => {
    if (sheet) clearToasts?.();
  }, [sheet, clearToasts]);

  // Keep the newest entry, and the checklist under it, in view as you log.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [loggedSoFar]);

  const effective = useMemo(() => withSession(list, log), [list, log]);
  // The checklist (#164): what's left of it comes first in the quick buttons
  // and, flagged in your list, in the suggestions.
  const left = useMemo(
    () => checklistProgress(checklistItems(checklist), log).filter((r) => r.left > 0),
    [checklist, log],
  );
  const ranked = useMemo(() => {
    const ids = new Set(left.map((r) => exerciseId(r.item.name)));
    return ids.size ? effective.map((e) => (ids.has(e.id) ? { ...e, onChecklist: true } : e)) : effective;
  }, [effective, left]);
  const picks = useMemo(() => {
    const first = left.map((r) => {
      const id = exerciseId(r.item.name);
      const known = ranked.find((e) => e.id === id);
      return { ...(known || { id, name: r.item.name, kind: r.item.kind, n: 0 }), last: checklistNext(r, ranked), checklistRow: r };
    });
    return quickPicks(ranked, log, 3, first);
  }, [ranked, log, left]);
  const parsed = useMemo(() => parseEntryText(query), [query]);
  // "tabata burpees": the block, of that move.
  // The name as typed, less its numbers. It's what Enter logs; a suggestion's
  // name is used only when you pick that suggestion.
  const typed = useMemo(() => typedName(query), [query]);
  const typedBlock = useMemo(() => splitBlockText(typed), [typed]);
  const options = useMemo(() => {
    if (typedBlock) {
      const ex = effective.find((e) => e.name === typedBlock.block) || {
        id: `block:${typedBlock.block.toLowerCase()}`,
        name: typedBlock.block,
        kind: "block",
        n: 0,
        last: defaultLast("block", typedBlock.block),
      };
      return [{ e: ex, of: typedBlock.of }];
    }
    const found = suggestExercises(ranked, parsed.text);
    const rows = found.map((e) => ({ e }));
    if (typed && !found.some((e) => e.name.toLowerCase() === typed.toLowerCase())) {
      // Recognised (your list or the library): its kind and numbers, your name.
      const ident = identifyExercise(parsed.text);
      const known = ranked.find((e) => e.id === ident.id);
      const as = known
        ? { ...known, name: typed }
        : ident.kind
          ? { id: ident.id, name: typed, kind: ident.kind, n: 0, last: defaultLast(ident.kind, parsed.text) }
          : null;
      rows.unshift({ typed, as });
    }
    return rows;
  }, [effective, ranked, parsed.text, typed, typedBlock]);
  const typedNumbers = hasNumbers(parsed);
  const showSuggest = open && query.trim() && options.length > 0;

  // Moves for a block: whatever you did last, your own, then the usual ones.
  const moves = useMemo(() => {
    const own = [...effective]
      .filter((e) => e.kind === "lift" || e.kind === "body")
      .sort((a, b) => b.n - a.n)
      .map((e) => e.name);
    return [...new Set([...own.slice(0, 3), ...DEFAULT_BLOCK_MOVES])].slice(0, 6);
  }, [effective]);

  useEffect(() => {
    if (!showSuggest) return undefined;
    const close = (e) => {
      if (!composerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [showSuggest]);

  const done = () => {
    setQuery("");
    setOpen(false);
    setSel(0);
  };

  const logSets = (ex, { kg, reps, s = null, count = 1 }) => {
    onChange((l, now) => addSets(l, { name: ex.name, kind: ex.kind, kg, reps, s, count }, now));
    toast(showToast, `${ex.name} · ${formatSet({ kg, reps, s })}${count > 1 ? ` · ${count} sets` : ""}`);
  };
  const logPiece = (ex, piece) => {
    onChange((l, now) => addPiece(l, { name: ex.name, ...piece }, now));
    toast(showToast, describeEntry({ name: ex.name, kind: "cardio", ...piece }));
  };
  const logBlock = (ex, block) => {
    onChange((l, now) => addBlock(l, { name: ex.name, ...block }, now));
    toast(showToast, describeEntry({ name: ex.name, kind: "block", ...block }));
  };

  // Log a checklist item: the number sheet, filled with its next numbers.
  const openChecklistRow = (row) => {
    const id = exerciseId(row.item.name);
    const known = effective.find((e) => e.id === id);
    const ex = { ...(known || { id, name: row.item.name, kind: row.item.kind, n: 0 }), last: checklistNext(row, effective) };
    const figure = checklistFigure(row.item);
    setSheet({
      type: "numbers",
      ex,
      parsed: {},
      description:
        row.planned > 1
          ? `Set ${row.done + 1} of ${row.planned} on your checklist`
          : `On your checklist${figure ? `: ${figure}` : ""}`,
    });
    done();
  };

  // Pick an exercise. Typed numbers log at once; otherwise the number sheet
  // opens, filled from the checklist when it's on it, else from last time.
  const choose = (ex, p = {}) => {
    const last = ex.last || defaultLast(ex.kind, ex.name);
    // A hold, for time: "plank 45s", "plank 3x45s", or "plank 3x45" read as
    // seconds.
    if (isHold(ex) && (p.s || p.reps != null)) {
      const setsFirst = !p.s && p.kg != null && !p.kgTyped && p.sets == null && p.kg <= 10;
      logSets(ex, {
        kg: setsFirst ? last.kg || 0 : typedLoadKg(p) ?? last.kg ?? 0,
        s: p.s || p.reps,
        count: setsFirst ? p.kg : p.sets || 1,
      });
      return done();
    }
    if ((ex.kind === "lift" || ex.kind === "body") && p.reps != null) {
      // "pull-up 3x10": on a bodyweight move a bare first number is sets.
      const setsFirst = ex.kind === "body" && p.kg != null && !p.kgTyped && p.sets == null;
      logSets(ex, {
        kg: setsFirst ? last.kg || 0 : typedLoadKg(p) ?? (ex.kind === "body" ? 0 : last.kg),
        reps: p.reps,
        count: setsFirst ? p.kg : p.sets || 1,
      });
      return done();
    }
    if (ex.kind === "cardio" && pieceFromParsed(p)) {
      logPiece(ex, pieceFromParsed(p));
      return done();
    }
    if (ex.kind === "block" && p.rounds) {
      logBlock(ex, { rounds: p.rounds, of: p.of || last.of || moves[0] });
      return done();
    }
    const row = left.find((r) => sameExercise(r.item.name, ex.name));
    if (row && !hasNumbers(p)) return openChecklistRow(row);
    setSheet({ type: "numbers", ex, parsed: p });
    done();
  };

  // What a typed entry is, by its name: your own list first, then the
  // library. Null for something new, which needs its kind asked.
  const exerciseFor = (text) => {
    const p = parseEntryText(text);
    const name = typedName(text);
    const ident = identifyExercise(p.text);
    const known = ranked.find((e) => e.id === ident.id);
    if (known) return { ...known, name };
    if (ident.kind) return { id: ident.id, name, kind: ident.kind, n: 0, last: defaultLast(ident.kind, p.text) };
    return null;
  };

  // Several entries in one go (#159): "bench 80x8, row 2k then plank 45s".
  // Only when every part is something known with its numbers; otherwise the
  // usual one-at-a-time path takes over.
  const logMany = (text) => {
    const parts = splitEntries(text);
    if (parts.length < 2) return false;
    const entries = parts.map((part) => ({ ex: exerciseFor(part), p: parseEntryText(part) }));
    if (!entries.every(({ ex, p }) => ex && ex.kind !== "block" && hasNumbers(p))) return false;
    for (const { ex, p } of entries) choose(ex, p);
    return true;
  };

  const pick = (row) => {
    if (row.e) choose(row.e, row.of ? { ...parsed, of: row.of } : parsed);
    else if (row.as) choose(row.as, parsed);
    else {
      // Something new: say what kind it is first.
      setSheet({ type: "new", name: row.typed, parsed });
      done();
    }
  };

  const onKeyDown = (e) => {
    if (!showSuggest) return;
    if (e.key === "ArrowDown") {
      setSel((i) => Math.min(i + 1, options.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setSel((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const closeSheet = () => setSheet(null);
  const sheetNode = sheet?.entryId ? log.find((e) => e.id === sheet.entryId) : null;
  const sheetEntry = sheetNode ? entryView(sheetNode) : null;

  return (
    <section aria-label="What you've done" className="flex min-h-0 w-full max-w-md flex-1 flex-col text-left">
      {/* The log scrolls; the ring above and the box below stay put (#164). */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {log.length > 0 && (
        <div className="mb-2 flex items-baseline justify-between px-0.5 text-xs text-muted-foreground">
          <b className="text-[13px] font-semibold text-foreground/70">This session</b>
          <span>{`${loggedCount(log)} logged · tap a name or number to fix it`}</span>
        </div>
      )}
      <div className="grid gap-2" aria-live="polite">
        {log.map((node, i) => {
          const entry = entryView(node);
          return (
          <LogEntry
            key={entry.id}
            entry={entry}
            current={i === log.length - 1}
            onEdit={() => setSheet({ type: "edit", entryId: entry.id })}
            onEditSet={(index) => setSheet({ type: "set", entryId: entry.id, index })}
            onAgain={() => {
              onChange((l, now) => repeatEntry(l, entry.id, now));
              toast(showToast, describeEntry(entry));
            }}
            onDuplicate={() => {
              onChange((l, now) => repeatEntry(l, entry.id, now));
              toast(showToast, describeEntry(entry));
            }}
            onDelete={() => {
              onChange((l) => deleteEntry(l, entry.id));
              toast(showToast, `${entry.name} removed`);
            }}
          />
          );
        })}
      </div>

      {/* What's left on the checklist (#164), in any order. A row logs it
          with its numbers; anything else logged still counts. */}
      {!checklistEmpty(checklist) && (
        <div className="mt-3">
          <Group title="Checklist" note={left.length ? `${left.length} left` : "All ticked off"}>
            {left.map((r) => {
              const figure = checklistFigure(r.item);
              return (
                <ChecklistRow
                  key={r.item.id}
                  item={r.item}
                  detail={r.done ? `${r.left} of ${r.planned} sets left` : figure ? null : "As last time"}
                  tag={r.done || !figure ? describeLast(r.item.kind, checklistNext(r, effective)) : figure}
                  onClick={() => openChecklistRow(r)}
                />
              );
            })}
            {left.length === 0 && (
              <div className="grid grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2.5 px-3 py-3 text-sm text-muted-foreground">
                <Check className="h-[18px] w-[18px] text-green-500" aria-hidden="true" />
                {"Everything's ticked off. Anything else you do still counts."}
              </div>
            )}
            {onChecklistChange && (
              <AddRow label="Add to checklist" onClick={() => setSheet({ type: "checklist" })} />
            )}
          </Group>
        </div>
      )}
      </div>

      {/* The composer, in thumb reach: it stays at the foot of the screen
          while the log scrolls under the dial. */}
      <div
        ref={composerRef}
        className="sticky bottom-0 z-20 mt-3 grid gap-2 border-t border-border/60 bg-background pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] pt-2"
      >
        {showSuggest && (
          <div
            role="listbox"
            id="free-log-suggestions"
            aria-label="Suggestions"
            className="absolute inset-x-0 bottom-[calc(100%-4px)] z-30 overflow-hidden rounded-2xl border border-border bg-popover shadow-[0_-12px_30px_rgba(0,0,0,0.5)]"
          >
            <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              {typedNumbers ? "Tap to log" : "Your exercises"}
            </p>
            {options.map((row, i) => {
              // A suggestion, or what you typed (first, in quotes, as it'll be
              // logged): recognised, or new.
              const ex = row.e || row.as;
              const style = ex ? kindStyle(ex.kind) : null;
              const Icon = style ? style.icon : Plus;
              return (
                <button
                  key={row.e ? row.e.id : "typed"}
                  type="button"
                  role="option"
                  aria-selected={i === sel}
                  onClick={() => pick(row)}
                  className={cn(
                    "grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-2.5 border-t border-border/60 px-3 py-2.5 text-left",
                    i === sel && "bg-muted/50",
                  )}
                >
                  <Icon className={cn("h-4 w-4", style ? style.text : "text-muted-foreground")} aria-hidden="true" />
                  <span className="truncate">
                    {row.e
                      ? row.of
                        ? `${row.e.name} · ${row.of}`
                        : row.e.name
                      : row.as
                        ? `“${row.typed}”`
                        : `Add “${row.typed}”`}
                    {(row.e?.onChecklist || row.e?.inSession) && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                        {row.e.onChecklist ? "On checklist" : "This session"}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "font-timer text-xs tabular-nums",
                      typedNumbers && ex ? "font-sans font-bold text-primary" : "text-muted-foreground",
                    )}
                  >
                    {ex ? (typedNumbers ? "Log" : describeLast(ex.kind, ex.last)) : "new"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {picks.length > 0 && (
          // One even row of three: name over its numbers, each cut short
          // rather than wrapping the row.
          <div className="grid grid-cols-3 gap-1.5" aria-label="Quick add">
            {picks.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => (e.checklistRow ? openChecklistRow(e.checklistRow) : choose(e))}
                aria-label={`${e.name}, ${describeLast(e.kind, e.last)}${e.checklistRow ? ", on your checklist" : ""}`}
                className="grid min-w-0 gap-0.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-left transition-colors hover:border-primary/40"
              >
                <span className="flex min-w-0 items-center gap-1 text-[13px] font-semibold leading-tight">
                  {e.checklistRow && (
                    <ListChecks className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <span className="truncate">{e.name}</span>
                </span>
                <span className="truncate font-timer text-[11px] leading-tight text-muted-foreground">
                  {describeLast(e.kind, e.last)}
                </span>
              </button>
            ))}
          </div>
        )}

        <form
          className="grid grid-cols-[minmax(0,1fr)_48px] gap-2"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (!query.trim()) {
              inputRef.current?.focus();
              return;
            }
            if (logMany(query)) return;
            const row = options[sel];
            if (row) pick(row);
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSel(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            enterKeyHint="done"
            placeholder="Add an exercise · bench 80x8, row 2k"
            role="combobox"
            aria-label="Add an exercise"
            aria-controls="free-log-suggestions"
            aria-autocomplete="list"
            aria-expanded={!!showSuggest}
            className="h-12 min-w-0 rounded-xl border border-border bg-card px-3.5 text-base outline-none placeholder:text-muted-foreground focus:border-primary/45 focus:ring-[3px] focus:ring-primary/15"
          />
          <Button type="submit" aria-label="Add" className="h-12 w-12 p-0 [&_svg]:size-6">
            <Plus />
          </Button>
        </form>
      </div>

      {sheet?.type === "numbers" && (
        <NumbersSheet
          ex={sheet.ex}
          parsed={sheet.parsed}
          description={sheet.description}
          moves={moves}
          pairedMachine={pairedMachine}
          onClose={closeSheet}
          onLog={(values) => {
            const { ex } = sheet;
            if (ex.kind === "cardio") logPiece(ex, values);
            else if (ex.kind === "block") logBlock(ex, values);
            else logSets(ex, values);
            closeSheet();
          }}
        />
      )}
      {sheet?.type === "new" && (
        <NewExerciseSheet
          name={sheet.name}
          parsed={sheet.parsed}
          onClose={closeSheet}
          onNext={(kind) => {
            // A timed hold is bodyweight done for time (it can carry a load).
            const ex =
              kind === "hold"
                ? { id: sheet.name.toLowerCase(), name: sheet.name, kind: "body", hold: true, n: 0, last: { kg: 0, s: 30 } }
                : { id: sheet.name.toLowerCase(), name: sheet.name, kind, n: 0, last: defaultLast(kind, sheet.name) };
            setSheet(null);
            choose(ex, sheet.parsed);
          }}
        />
      )}
      {sheet?.type === "set" && sheetEntry?.sets?.[sheet.index] && (
        <SetSheet entry={sheetEntry} index={sheet.index} onClose={closeSheet} onChange={onChange} />
      )}
      {sheet?.type === "edit" && sheetNode && (
        <EntryStepDialog
          node={sheetNode}
          onClose={closeSheet}
          onChange={onChange}
          onDuplicate={() => {
            onChange((l, now) => repeatEntry(l, sheetNode.id, now));
            toast(showToast, describeEntry(sheetNode));
          }}
        />
      )}
      {sheet?.type === "checklist" && onChecklistChange && (
        <ChecklistSheet
          checklist={checklist}
          onChange={onChecklistChange}
          list={effective}
          sessions={checklistSessions}
          onClose={closeSheet}
        />
      )}
    </section>
  );
}
